import { forwardRef } from "react";
import { cn } from "@/shared/lib/cn";
import { ScrollGlobeActions } from "./ScrollGlobeActions";
import { ScrollGlobeFeature } from "./ScrollGlobeFeature";
import type { ScrollGlobeSection } from "./scrollGlobe.types";

export interface ScrollGlobeSectionBlockProps {
  section: ScrollGlobeSection;
  /** La primera sección lleva el título grande: es la portada. */
  isFirst: boolean;
}

/**
 * Una sección de la landing inmersiva: alto de pantalla, título grande,
 * bajada y —según el caso— tarjetas, botones o un cuerpo a medida.
 *
 * ── El badge no se pinta aquí ────────────────────────────────────────
 * Es deliberado y viene del sistema actual: la etiqueta de cada sección
 * solo vive en la navegación lateral. Dentro, sobre un título de 60 px,
 * sobraba.
 *
 * ── Los títulos son `<h2>` menos el primero ──────────────────────────
 * La portada es el `<h1>` de la página. Las otras tres cuelgan de ella,
 * así que van en `<h2>` y el documento tiene un esquema de encabezados
 * navegable en vez de cuatro títulos del mismo rango sin jerarquía.
 *
 * ── `content` sustituye el cuerpo, no la sección ─────────────────────
 * Cuando una sección trae cuerpo a medida (la tarjeta de compra), no se
 * pintan ni título ni bajada ni botones: la tarjeta ya los trae dentro.
 * Y baja el alto mínimo, porque a pantalla completa el hueco que quedaba
 * antes del pie se veía como un error de maquetación.
 */
export const ScrollGlobeSectionBlock = forwardRef<
  HTMLElement,
  ScrollGlobeSectionBlockProps
>(function ScrollGlobeSectionBlock({ section, isFirst }, ref) {
  const align = section.align ?? "left";
  const Heading = isFirst ? "h1" : "h2";

  return (
    <section
      ref={ref}
      id={section.id}
      aria-label={section.badge}
      className={cn(
        "relative z-20 flex w-full max-w-full flex-col justify-center overflow-hidden",
        "px-4 py-12 sm:px-6 sm:py-16 md:px-8 lg:px-12 lg:py-20",
        section.content ? "min-h-[70vh]" : "min-h-screen",
        align === "center" && "items-center text-center",
        align === "right" && "items-end text-right",
        align === "left" && "items-start text-left",
      )}
    >
      <div className="w-full max-w-sm sm:max-w-lg md:max-w-2xl lg:max-w-4xl xl:max-w-5xl">
        {section.content ?? (
          <>
            <Heading
              className={cn(
                "mb-6 font-light leading-[1.08] tracking-tight sm:mb-8",
                isFirst
                  ? "text-4xl sm:text-5xl lg:text-6xl xl:text-7xl"
                  : "text-3xl sm:text-4xl lg:text-5xl",
              )}
            >
              <span className="text-ds-ink">{section.title}</span>
              {section.subtitle && (
                <span className="mt-1 block text-[0.62em] font-medium tracking-wide text-ds-violet-pop">
                  {section.subtitle}
                </span>
              )}
            </Heading>

            {section.description && (
              <p
                className={cn(
                  "mb-8 text-[16px] font-light leading-relaxed text-ds-ink-3 sm:mb-10 sm:text-[18px] lg:text-[20px]",
                  align === "center" ? "mx-auto max-w-2xl" : "max-w-xl",
                )}
              >
                {section.description}
              </p>
            )}

            {section.features && section.features.length > 0 && (
              <ul className="mb-8 grid gap-3 sm:mb-10 sm:gap-4">
                {section.features.map((feature) => (
                  <ScrollGlobeFeature key={feature.title} {...feature} />
                ))}
              </ul>
            )}

            {section.actions && section.actions.length > 0 && (
              <ScrollGlobeActions actions={section.actions} align={align} />
            )}
          </>
        )}
      </div>
    </section>
  );
});
